import React, { useMemo } from 'react';
import { Building2, MapPin, Users, Check, Circle } from 'lucide-react';
import { Card, CardContent } from '../UI/card';
import { Badge } from '../UI/badge';
import { Button } from '../UI/button';
import { StatCard } from '../UI/stat-card';
import { useLocale } from '../../contexts/LocaleContext';
import { themeClasses } from '../../config/themeClasses';
import {
  TENANT_DATA_HUB_TAB,
  TENANT_DATA_IMPORT_SECTION,
} from '../../config/tenantNodeTabsConfig';
import {
  computeCompletionChecklist,
  computeNodeStats,
  computeProfileCompletion,
} from '../../utils/tenantMapModel';
import { cn } from '../../lib/utils';

const CHECKLIST_LABEL_KEYS = {
  name: 'tenantChecklistAddName',
  ticker: 'tenantChecklistAddTicker',
  facilities: 'tenantChecklistImportFacilities',
  geographies: 'tenantChecklistAddGeographies',
};

function getInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function TenantCompanyProfileHero({
  profile,
  nodesByKey,
  supplierCount = 0,
  loading,
  overallRiskScore,
  onNavigate,
}) {
  const { t } = useLocale();
  const stats = useMemo(() => computeNodeStats(nodesByKey), [nodesByKey]);
  const completion = useMemo(
    () => computeProfileCompletion(profile || {}, nodesByKey),
    [profile, nodesByKey]
  );
  const checklist = useMemo(
    () => computeCompletionChecklist(profile || {}, nodesByKey),
    [profile, nodesByKey]
  );

  const displayName = profile?.name || t('tenantHeroUnnamed');
  const location = [profile?.city, profile?.country].filter(Boolean).join(', ');
  const employeeCount = profile?.employee_count;

  const kpiCards = [
    { key: 'facilities', labelKey: 'tenantStatFacilities', value: stats.facilities },
    {
      key: 'supplierFacilities',
      labelKey: 'tenantStatSupplierFacilities',
      value: stats.supplierFacilities,
    },
    { key: 'geographies', labelKey: 'tenantStatGeographies', value: stats.geographies },
  ];

  const handleChecklistClick = (item) => {
    if (item.done || !onNavigate) return;
    onNavigate({
      primary: item.targetPrimary,
      dataSection: item.targetDataSection || undefined,
    });
  };

  return (
    <Card className={themeClasses.appKit.sectionShell}>
      <CardContent className="p-6">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:items-start">
            <div
              className={cn(
                'flex size-16 shrink-0 items-center justify-center rounded-lg border-2',
                'border-primary-200 bg-primary-50 text-xl font-bold text-primary-600'
              )}
              aria-hidden
            >
              {getInitials(displayName)}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-semibold tracking-tight text-text-primary">{displayName}</h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {profile?.industry ? <Badge variant="secondary">{profile.industry}</Badge> : null}
                {profile?.stock_ticker ? (
                  <Badge variant="outline">{profile.stock_ticker}</Badge>
                ) : null}
                {overallRiskScore != null && overallRiskScore > 0 ? (
                  <Badge variant="secondary">
                    {t('tenantHeroRiskScore', { score: Math.round(overallRiskScore) })}
                  </Badge>
                ) : null}
              </div>
              <div className="mt-3 flex flex-col gap-1 text-sm text-text-secondary">
                {location ? (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 shrink-0" />
                    {location}
                  </span>
                ) : null}
                {profile?.legal_name ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="h-4 w-4 shrink-0" />
                    {profile.legal_name}
                  </span>
                ) : null}
                {employeeCount != null && String(employeeCount) !== '' ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="h-4 w-4 shrink-0" />
                    {t('tenantHeroEmployees', { count: employeeCount })}
                  </span>
                ) : null}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onNavigate?.({ primary: 'overview' })}
                >
                  {t('tenantQuickActionEditProfile')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    onNavigate?.({
                      primary: TENANT_DATA_HUB_TAB,
                      dataSection: TENANT_DATA_IMPORT_SECTION,
                    })
                  }
                >
                  {t('tenantQuickActionImportData')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onNavigate?.({ primary: 'map' })}
                >
                  {t('tenantQuickActionViewMap')}
                </Button>
                {overallRiskScore != null ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onNavigate?.({ primary: 'analysis' })}
                  >
                    {t('tenantQuickActionViewRisk')}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <aside className="w-full shrink-0 xl:w-64">
            <p className="text-sm font-medium text-text-secondary">{t('tenantChecklistTitle')}</p>
            <p className="mt-1 text-xs text-text-tertiary">
              {loading ? '—' : `${completion}% · ${t('tenantProfileCompletion')}`}
            </p>
            <ul className="mt-3 space-y-2">
              {checklist.map((item) => {
                const labelKey = CHECKLIST_LABEL_KEYS[item.id];
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      disabled={item.done}
                      onClick={() => handleChecklistClick(item)}
                      className={cn(
                        'flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
                        item.done
                          ? 'text-text-tertiary'
                          : 'text-text-primary hover:bg-primary-50'
                      )}
                    >
                      {item.done ? (
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-success-500" aria-hidden />
                      ) : (
                        <Circle className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
                      )}
                      <span className="min-w-0 flex-1">
                        {labelKey ? t(labelKey) : item.id}
                        <span className="sr-only">
                          {item.done ? t('tenantChecklistDone') : t('tenantChecklistTodo')}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {kpiCards.map((stat) => (
            <StatCard
              key={stat.key}
              title={t(stat.labelKey)}
              value={stat.value}
              className="!p-4"
            />
          ))}
          <StatCard
            title={t('tenantStatSuppliers')}
            value={loading ? '—' : supplierCount}
            className="!p-4"
          />
        </div>
      </CardContent>
    </Card>
  );
}

export default TenantCompanyProfileHero;
